// @ts-check
/** Country / PIN code / city / state for an order's delivery address. For India the PIN code looks up
 * the city options and GST state, and both are dropdowns, so staff pick instead of typing. A failed
 * lookup (or a non-Indian address) falls back to plain inputs rather than blocking the edit. */
import { useEffect, useState } from 'react';
import adminOrderService from '../services/adminOrderService';
import { COUNTRIES } from '../utils/countries';
import { GST_STATE_NAMES } from '../utils/gstStates';

const field = 'px-3 py-2 border border-input rounded-lg';
const INDIA = 'India';
const STATES = GST_STATE_NAMES.filter((s) => s !== 'Other Country' && s !== 'Other Territory');

/** @param {string[]} options @param {string} current */
const withCurrent = (options, current) => (current && !options.includes(current) ? [current, ...options] : options);

/**
 * @param {{form: Record<string, string>, onChange: (patch: Record<string, string>) => void}} props
 */
const DeliveryAddressFields = ({ form, onChange }) => {
  const isIndia = form.shipping_country === INDIA;
  const [cities, setCities] = useState(/** @type {string[]} */ ([]));
  const [lookupNote, setLookupNote] = useState('');
  const pin = form.shipping_postal_code;

  useEffect(() => {
    setLookupNote('');
    if (!isIndia || !/^[1-9][0-9]{5}$/.test(pin)) {
      setCities([]);
      return undefined;
    }
    let cancelled = false;
    adminOrderService
      .lookupPincode(pin)
      .then(({ state, cities: found }) => {
        if (cancelled) return;
        setCities(found);
        onChange({
          shipping_state: state,
          ...(found.includes(form.shipping_city) ? {} : { shipping_city: found[0] || '' }),
        });
      })
      .catch((err) => {
        if (cancelled) return;
        setCities([]);
        setLookupNote(err.message || 'Could not look up this PIN code');
      });
    return () => {
      cancelled = true;
    };
    // Only a changed PIN (or country) should trigger a lookup, not every keystroke elsewhere.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pin, isIndia]);

  const countryNames = withCurrent(
    COUNTRIES.map((c) => c.name),
    form.shipping_country,
  );

  return (
    <>
      <select
        aria-label="Country"
        value={form.shipping_country}
        onChange={(e) => onChange({ shipping_country: e.target.value, shipping_state: '', shipping_city: '' })}
        className={field}
      >
        <option value="">Country</option>
        {countryNames.map((name) => (
          <option key={name} value={name}>
            {name}
          </option>
        ))}
      </select>
      <input
        aria-label="Postal code"
        placeholder={isIndia ? 'PIN code (fills city and state)' : 'Postal code'}
        inputMode={isIndia ? 'numeric' : undefined}
        maxLength={isIndia ? 6 : undefined}
        value={form.shipping_postal_code}
        onChange={(e) =>
          onChange({ shipping_postal_code: isIndia ? e.target.value.replace(/\D/g, '') : e.target.value })
        }
        className={field}
      />
      {isIndia && cities.length > 0 ? (
        <select
          aria-label="City"
          value={form.shipping_city}
          onChange={(e) => onChange({ shipping_city: e.target.value })}
          className={field}
        >
          {withCurrent(cities, form.shipping_city).map((name) => (
            <option key={name} value={name}>
              {name}
            </option>
          ))}
        </select>
      ) : (
        <input
          aria-label="City"
          placeholder="City"
          value={form.shipping_city}
          onChange={(e) => onChange({ shipping_city: e.target.value })}
          className={field}
        />
      )}
      {isIndia ? (
        <select
          aria-label="State"
          value={form.shipping_state}
          onChange={(e) => onChange({ shipping_state: e.target.value })}
          title="Locked once this order has a GST invoice - it already fixed the CGST/SGST vs IGST split"
          className={field}
        >
          <option value="">State</option>
          {withCurrent(STATES, form.shipping_state).map((name) => (
            <option key={name} value={name}>
              {name}
            </option>
          ))}
        </select>
      ) : (
        <input
          aria-label="State"
          placeholder="State / region"
          value={form.shipping_state}
          onChange={(e) => onChange({ shipping_state: e.target.value })}
          className={field}
        />
      )}
      {lookupNote && <p className="col-span-2 text-xs text-muted-foreground">{lookupNote}</p>}
    </>
  );
};

export default DeliveryAddressFields;
